import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import AlertModal from "../AlertModal";
import { useAccount, useSecretNetworkClient } from "../../models/account";
import {
  P2PConfig,
  P2PPaymentInfo,
  registerP2PPaymentInfo,
} from "../../models/p2p";

export const paymentOptions = [
  "Ethereum Transfer",
  "Wire Transfer",
  "Paypal",
  "Other",
];

interface Props {
  open: boolean;
  onClose: () => void;
  config?: P2PConfig;
  paymentInfo?: P2PPaymentInfo;
  refetch?: () => void;
}

const RegisterP2PModal: FC<Props> = ({
  open,
  onClose,
  config,
  paymentInfo,
  refetch,
}) => {
  const secretjs = useSecretNetworkClient();
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetail, setPaymentDetail] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [paymentMethodSelect, setPaymentMethodSelect] = useState(
    paymentOptions[0]
  );

  useEffect(() => {
    setPaymentMethod(paymentInfo?.method || "");
    setPaymentMethodSelect(
      paymentOptions.includes(paymentInfo?.method || "")
        ? paymentInfo?.method || "Other"
        : "Other"
    );
    setPaymentDetail(paymentInfo?.detail || "");
    setContactInfo(paymentInfo?.contact || "");
  }, [paymentInfo]);

  useEffect(() => {
    if (refetch && (success || error)) {
      refetch();
    }
  }, [refetch, success, error]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!secretjs) {
              return;
            }
            try {
              setError("");
              setLoading(true);

              await registerP2PPaymentInfo(
                {
                  contract_address: config?.contract_address || "",
                  code_hash: config?.code_hash || "",
                  address: account.address,
                  method: JSON.stringify({
                    method:
                      paymentMethodSelect === "Other"
                        ? paymentMethod
                        : paymentMethodSelect,
                    contact: contactInfo,
                  }),
                  detail: paymentDetail,
                },
                secretjs
              );
              setLoading(false);
              setSuccess(`You have successfully updated your payment info.`);
              onClose();
            } catch (err: any) {
              setLoading(false);
              setError(err.message);
            }
          }}
        >
          <DialogTitle>Register Payment Info</DialogTitle>
          <DialogContent>
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Contact</Typography>
            </Stack>
            <TextField
              value={contactInfo}
              onChange={(e) => {
                setContactInfo(e.target.value);
              }}
              placeholder="eg. TG: @finacash"
              fullWidth
            />
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Payment Method</Typography>
            </Stack>
            <Autocomplete<string, false | undefined, true>
              options={paymentOptions}
              autoHighlight
              disableClearable
              componentsProps={{
                popper: { sx: { left: 0 } },
              }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password", // disable autocomplete and autofill
                  }}
                />
              )}
              value={paymentMethodSelect}
              onChange={(e, v) => setPaymentMethodSelect(v)}
            />
            {paymentMethodSelect === "Other" && (
              <TextField
                sx={{ mt: 2 }}
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                }}
                placeholder="Please specify"
                fullWidth
              />
            )}
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">
                Payment Information (Encrypted)
              </Typography>
            </Stack>
            <TextField
              value={paymentDetail}
              onChange={(e) => {
                setPaymentDetail(e.target.value);
              }}
              placeholder="eg. 0x123123..."
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ mr: 1 }}
              variant="text"
              size="large"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              disabled={
                loading ||
                !paymentMethodSelect ||
                (paymentMethodSelect === "Other" && !paymentMethod) ||
                !paymentDetail ||
                !contactInfo
              }
              size="large"
              type="submit"
            >
              {loading ? (
                <CircularProgress sx={{ mx: 1.5 }} size={25} />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <AlertModal
        open={!!success}
        onClose={() => setSuccess("")}
        title="Success"
        description={success}
        type="success"
      />
      <AlertModal
        open={!!error}
        onClose={() => setError("")}
        title="Error"
        description={error}
        type="error"
      />
    </>
  );
};

export default RegisterP2PModal;
